import { useMemo, type ReactNode } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ColGroupDef, ModuleRegistry, AllCommunityModule, CellValueChangedEvent, CellEditingStartedEvent, CellEditingStoppedEvent, ValueParserParams } from "ag-grid-community";
import { AssignmentContextRow  } from "../types/PluginTypes";

ModuleRegistry.registerModules([AllCommunityModule]);

type Props = {
  rows: AssignmentContextRow[];
  onCoverageChange?: (row: AssignmentContextRow, decimalCoverage: string) => void;
  onCoverageEditingChange?: (isEditing: boolean) => void;
};

function normalizeDecimalString(value: number): string {
  return value.toFixed(6).replace(/\.?0+$/, "");
}

function parseCoveragePercentToDecimal(rawValue: unknown): string | null {
  if (rawValue == null) {
    return "";
  }

  const normalizedInput = String(rawValue).trim();
  if (normalizedInput.length === 0) {
    return "";
  }

  const numericString = normalizedInput
    .replace("%", "")
    .replace(",", ".")
    .trim();

  const parsedPercent = Number(numericString);
  if (!Number.isFinite(parsedPercent)) {
    return null;
  }

  return normalizeDecimalString(parsedPercent / 100);
}

function renderExponentText(value: string): ReactNode {
  const parts: ReactNode[] = [];
  const exponentRegex = /\^(\d+)/g;
  let match: RegExpExecArray | null;
  let lastIndex = 0;

  while ((match = exponentRegex.exec(value)) !== null) {
    if (match.index > lastIndex) {
      parts.push(value.slice(lastIndex, match.index));
    }

    parts.push(
      <sup
        key={`${match.index}-${match[1]}`}
        style={{ fontSize: "0.7em", lineHeight: 1, verticalAlign: "super" }}
      >
        {match[1]}
      </sup>
    );

    lastIndex = exponentRegex.lastIndex;
  }

  if (lastIndex < value.length) {
    parts.push(value.slice(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : value;
}

export default function AssignmentContextTable({ rows, onCoverageChange, onCoverageEditingChange }: Props) {
  const isEmpty = rows.length === 0;
  const tableHeight = isEmpty ? 93 : undefined;
  // const headerRowHeight = 42;
  // const groupHeaderRowHeight = 42;
  // const rowHeight = 42;
  // const tableHeight = isEmpty
  //   ? headerRowHeight + groupHeaderRowHeight + rowHeight
  //   : undefined;
  const domLayout = isEmpty ? "normal" : "autoHeight";

  const noRowsOverlayTemplate = `
    <div style="
      margin: 4px 12px;
      padding: 5px 8px;
      border: 1px solid #f59e0b;
      background: #fff8e1;
      color: #7c2d12;
      border-radius: 8px;
      font-weight: 600;
      font-size: 14px;
      text-align: center;
    ">
      No models selected
    </div>
  `;

  const columnDefs = useMemo<(ColDef<AssignmentContextRow> | ColGroupDef<AssignmentContextRow>)[]>(
    () => [
        {
            headerName: "Model Name",
            field: "modelName",
            flex: 1,
            sortable: false,
            filter: false,
            resizable: true,
        },
        // {
        //     headerName: "bmId",
        //     field: "bmId",
        //     flex: 1,
        //     sortable: false,
        //     filter: false,
        //     resizable: true,
        // },
        {
            headerName: "Material",
            field: "bmName",
            flex: 1,
            sortable: false,
            filter: false,
            resizable: true,
        },
        {
            headerName: "Density",
            field: "bmDensity",
            flex: 1,
            sortable: false,
            filter: false,
            resizable: true,
          cellRenderer: (params: { value?: string }) => renderExponentText(params.value ?? ""),
        },
        {
            headerName: "Surface Treatment",
            field: "stName",
            flex: 1,
            sortable: false,
            filter: false,
            resizable: true,
        },
        {
            headerName: "Coverage",
            field: "stCoverage",
            flex: 1,
            sortable: false,
            filter: false,
            resizable: true,
            editable: true,
            cellClass: "coverage-editable-cell",
            valueParser: (params: ValueParserParams<AssignmentContextRow>) => {
              const parsedCoverage = parseCoveragePercentToDecimal(params.newValue);
              if (parsedCoverage === null) {
                return params.oldValue;
              }

              return parsedCoverage;
            },
        },
        {
            headerName: "Thickness",
            field: "stThickness",
            flex: 1,
            sortable: false,
            filter: false,
            resizable: true,
        },
        // Group fallback if grouped Surface Treatment columns are needed again:
        // {
        //     headerName: "Surface Treatment",
        //     marryChildren: true,
        //     children: [
        //       {
        //         headerName: "Name",
        //         field: "stName",
        //         flex: 1,
        //         sortable: false,
        //         filter: false,
        //         resizable: true,
        //       },
        //       {
        //         headerName: "Coverage",
        //         field: "stCoverage",
        //         flex: 1,
        //         sortable: false,
        //         filter: false,
        //         resizable: true,
        //         editable: true,
        //         cellClass: "coverage-editable-cell",
        //         valueParser: (params: ValueParserParams<AssignmentContextRow>) => {
        //           const parsedCoverage = parseCoveragePercentToDecimal(params.newValue);
        //           if (parsedCoverage === null) {
        //             return params.oldValue;
        //           }
        //
        //           return parsedCoverage;
        //         },
        //         columnGroupShow: "open",
        //       },
        //       {
        //         headerName: "Thickness",
        //         field: "stThickness",
        //         flex: 1,
        //         sortable: false,
        //         filter: false,
        //         resizable: true,
        //         columnGroupShow: "open",
        //       },
        //     ],
        // },
        // {
        //     headerName: "ST Density",
        //     field: "stDensity",
        //     flex: 1,
        //     sortable: false,
        //     filter: false,
        //     resizable: true,
        //   cellRenderer: (params: { value?: string }) => renderExponentText(params.value ?? ""),
        // },
        {
            headerName: "Alt. Material Name",
            field: "altMatName",
            flex: 1,
            sortable: false,
            filter: false,
            resizable: true,
            wrapText: false,
            autoHeight: true,
            cellStyle: { whiteSpace: "pre", overflow: "hidden", textOverflow: "clip" },
        }
        // Group fallback if Alt. Material ID and Alt. Condition ID are needed again:
        // {
        //   headerName: "Alternate Materials",
        //   marryChildren: true,
        //   children: [
        //     {
        //       headerName: "",
        //       valueGetter: () => "",
        //       minWidth: 120,
        //       maxWidth: 200,
        //       sortable: false,
        //       filter: false,
        //       resizable: true,
        //       columnGroupShow: "closed",
        //     },
        //     {
        //       headerName: "Alt. Material ID",
        //       field: "altMatId",
        //       flex: 1,
        //       sortable: false,
        //       filter: false,
        //       resizable: true,
        //       wrapText: false,
        //       autoHeight: true,
        //       cellStyle: { whiteSpace: "pre", overflow: "hidden", textOverflow: "clip" },
        //       columnGroupShow: "open",
        //     },
        //     {
        //       headerName: "Alt. Material Name",
        //       field: "altMatName",
        //       flex: 1,
        //       sortable: false,
        //       filter: false,
        //       resizable: true,
        //       wrapText: false,
        //       autoHeight: true,
        //       cellStyle: { whiteSpace: "pre", overflow: "hidden", textOverflow: "clip" },
        //       columnGroupShow: "open",
        //     },
        //     {
        //       headerName: "Alt. Condition ID",
        //       field: "altConditionId",
        //       flex: 1,
        //       sortable: false,
        //       filter: false,
        //       resizable: true,
        //       wrapText: false,
        //       autoHeight: true,
        //       cellStyle: { whiteSpace: "pre", overflow: "hidden", textOverflow: "clip" },
        //       columnGroupShow: "open",
        //     },
        //   ],
        // }
    ],
    []
  );

  const rowData = rows;

  return (
    <div className="ag-theme-alpine plugin-table" style={{ height: tableHeight }}>
      <AgGridReact<AssignmentContextRow>
        rowData={rowData}
        columnDefs={columnDefs}
        getRowId={(params) => params.data.modelName}
        // headerHeight={headerRowHeight}
        // groupHeaderHeight={groupHeaderRowHeight}
        // rowHeight={rowHeight}
        stopEditingWhenCellsLoseFocus={true}
        domLayout={domLayout}
        overlayNoRowsTemplate={noRowsOverlayTemplate}
        onCellEditingStarted={(event: CellEditingStartedEvent<AssignmentContextRow>) => {
          if (event.colDef.field === "stCoverage") {
            onCoverageEditingChange?.(true);
          }
        }}
        onCellEditingStopped={(event: CellEditingStoppedEvent<AssignmentContextRow>) => {
          if (event.colDef.field === "stCoverage") {
            onCoverageEditingChange?.(false);
          }
        }}
        onCellValueChanged={(event: CellValueChangedEvent<AssignmentContextRow>) => {
          if (event.colDef.field !== "stCoverage" || !event.data) {
            return;
          }

          const nextCoverage = String(event.data.stCoverage ?? "");
          onCoverageChange?.(event.data, nextCoverage);
        }}
      />
    </div>
  );
}